import { makeAutoObservable } from "mobx";
import { doGET } from "../../../util/HttpUtil";
import { parseDialCodes } from "../../../util/dialCode";

const normalizeCountryMasterCountry = (country) => {
  const dialCodes = parseDialCodes(
    country?.["Country Calling Code"] ?? country?.countryCallingCode
  );
  const primaryDialCode = dialCodes[0] ?? null;

  return {
    ...country,
    label: country?.Country,
    value: country?.Alpha2Code,
    name: country?.Country,
    isoCode2: country?.Alpha2Code,
    isoCode3: country?.Alpha3Code,
    isd: primaryDialCode,
    isdCodes: dialCodes,
    countryCallingCode: primaryDialCode,
    countryCallingCodes: dialCodes,
    nationality: country?.Nationality,
    flag: null,
  };
};

class CountrySelect {
  countries = [];
  cyclosCountryMaster = [];
  cyclosCountryMasterMap = {};
  ipCallingCode = null;
  ipCallingCodePromise = null;
  COUNTRY_MASTER_KEYS = {
    COUNTRY : "Country",
    BANK_ACCOUNT: "Bank Account",
    CRYPTO_CUSTODY: "Crypto Custody",
    VISA_CARD: "Visa Card",
    COUNTRY_CALLING_CODE: "Country Calling Code",
    NATIONALITY: "Nationality",
    OPTIONAL_MAXIMUM_DEPOSIT_VIA_STRIPE: "Optional Maximum Deposit Via Stripe",
    OPTIONAL_MINIMUM_INITIAL_DEPOSIT_VIA_STRIPE: "Optional Minimum Initial Deposit Via Stripe",
    RISK_RATING: "Risk Rating",
    ALPHA3_CODE: "Alpha3Code",
    ALPHA2_CODE: "Alpha2Code",
    REGION : "Region",

    I_PLUS_APPLICATION_FEE: "IPlus Application Fee",
    I_PLUS_CARD_FEE: "IPlus Card Fee",
    I_PLUS_ACCOUNT_OPENING_FEE: "IPlus Account Opening Fee",

    B_PLUS_APPLICATION_FEE: "BPlus Application Fee",
    B_PLUS_CARD_FEE: "BPlus Card Fee",
    B_PLUS_ACCOUNT_OPENING_FEE: "BPlus Account Opening Fee",

    B_PRIME_APPLICATION_FEE: "BPrime Application Fee",
    B_PRIME_ACCOUNT_OPENING_FEE: "BPrime Account Opening Fee",
    B_PRIME_ACCOUNT_UPGRADE_FEE : "BPrime Account Upgrade Fee",
    B_PRIME_CARD_FEE: "BPrime Card Fee",

    I_PRIME_APPLICATION_FEE: "IPrime Application Fee",
    I_PRIME_ACCOUNT_OPENING_FEE: "IPrime Account Opening Fee",
    I_PRIME_ACCOUNT_UPGRADE_FEE: "IPrime Account Upgrade Fee",
    I_PRIME_CARD_FEE: "IPrime Card Fee",

    SANCTION_COUNTRY: "Sanction Country",
  }

  constructor() {
    makeAutoObservable(this);
  }

  setCountryMasterData = (rawList = []) => {
    const list = rawList
      ?.map(normalizeCountryMasterCountry)
      ?.filter((country) => country?.label && country?.value)
      ?.sort((a, b) => a.label.localeCompare(b.label));

    this.countries = list;
    this.cyclosCountryMaster = list;
    this.cyclosCountryMasterMap = list?.reduce((acc, country) => {
      const alpha2Code = country?.Alpha2Code || country?.isoCode2;
      if (alpha2Code) {
        acc[alpha2Code] = country;
      }
      return acc;
    }, {});

    return list;
  };

  fetch = async function () {
    try {
      if (this.countries.length > 0) return this.countries;
      return this.fetchCyclosCountryMaster();
    } catch (err) {
      this.error = err;
    }
  };

  fetchCyclosCountryMaster = async () => {
    try {
      if (this.cyclosCountryMaster.length > 0) return this.cyclosCountryMaster;
      const response = await doGET(`/api/o/cyclos/country/master`);
      const list = Array.isArray(response?.data?.rows)
        ? response.data.rows
        : Array.isArray(response?.data)
          ? response.data
          : [];
      return this.setCountryMasterData(list);
    } catch (error) {
      console.log(error);
    }
  };

  fetchIpCallingCode = async () => {
    if (this.ipCallingCode) {
      return this.ipCallingCode;
    }

    if (this.ipCallingCodePromise) {
      return this.ipCallingCodePromise;
    }

    this.ipCallingCodePromise = fetch('https://ipapi.co/json')
      .then(async (response) => {
        const data = await response.json();
        const nextCode = parseDialCodes(data?.country_calling_code)?.[0] || '86';
        this.ipCallingCode = nextCode;
        return nextCode;
      })
      .catch(() => {
        this.ipCallingCode = '86';
        return this.ipCallingCode;
      })
      .finally(() => {
        this.ipCallingCodePromise = null;
      });

    return this.ipCallingCodePromise;
  };

  getIsoCodesForDialCode = (dialCode) => {
    const normalizedDialCodes = parseDialCodes(dialCode);
    if (!normalizedDialCodes.length) return [];

    return [...new Set(this.cyclosCountryMaster
      ?.filter((country) =>
        (country?.isdCodes || (country?.isd ? [country.isd] : []))
          ?.some((countryDialCode) => normalizedDialCodes.includes(countryDialCode))
      )
      ?.map((country) => country?.isoCode2)
      ?.filter(Boolean))];
  };
}

const CountrySelectService = new CountrySelect();
export default CountrySelectService;
